import { useEffect, useState } from "react";
import DashboardShell, { StatCard } from "@/components/DashboardShell";
import { api, fmtNGN, fmtBps, formatErr } from "@/lib/api";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Legend } from "recharts";
import { TestTube, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const DEFAULT_INPUTS = {
  num_parents: 1000,
  months: 12,
  avg_fee_per_term_ngn: 200000,
  terms_per_year: 3,
  loan_uptake_pct: 30,
  avg_loan_tenor_months: 6,
  default_rate_pct: 8,
  avg_wallet_balance_ngn: 80000,
  dsa_attribution_pct: 70,
};

const SLIDERS = [
  { k: "num_parents", label: "Parents in cohort", min: 100, max: 50000, step: 100, fmt: (v) => v.toLocaleString() },
  { k: "months", label: "Projection horizon (months)", min: 1, max: 36, step: 1, fmt: (v) => `${v} mo` },
  { k: "avg_fee_per_term_ngn", label: "Avg school fee / term", min: 30000, max: 1000000, step: 5000, fmt: (v) => fmtNGN(v) },
  { k: "terms_per_year", label: "Terms / year", min: 1, max: 4, step: 1, fmt: (v) => v },
  { k: "loan_uptake_pct", label: "Parents who finance fees (%)", min: 0, max: 100, step: 1, fmt: (v) => `${v}%` },
  { k: "avg_loan_tenor_months", label: "Avg loan tenor", min: 1, max: 12, step: 1, fmt: (v) => `${v} mo` },
  { k: "default_rate_pct", label: "Default rate (%)", min: 0, max: 30, step: 0.5, fmt: (v) => `${v}%` },
  { k: "avg_wallet_balance_ngn", label: "Avg wallet balance", min: 0, max: 500000, step: 1000, fmt: (v) => fmtNGN(v) },
  { k: "dsa_attribution_pct", label: "Parents attributed to DSA (%)", min: 0, max: 100, step: 5, fmt: (v) => `${v}%` },
];

const FEE_OVERRIDES = [
  { k: "platform_txn_fee_bps", label: "Txn fee (bps)", placeholder: "e.g., 150" },
  { k: "platform_late_fee_bps", label: "Late fee (bps)", placeholder: "e.g., 200" },
  { k: "dsa_commission_bps", label: "DSA commission (bps)", placeholder: "e.g., 125" },
  { k: "wallet_float_spread_bps", label: "Wallet float (bps)", placeholder: "e.g., 100" },
  { k: "default_loan_interest_bps", label: "Loan APR (bps)", placeholder: "e.g., 350" },
];

export default function AdminStressTest() {
  const [inputs, setInputs] = useState(DEFAULT_INPUTS);
  const [overrides, setOverrides] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const payload = { ...inputs };
      for (const k in overrides) if (overrides[k] !== "" && overrides[k] != null) payload[k] = Number(overrides[k]);
      const { data } = await api.post("/admin/stress-test", payload);
      setResult(data);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { run(); }, []); // eslint-disable-line

  const t = result?.totals;
  const monthly = result?.monthly || [];

  return (
    <DashboardShell title="Stress test" subtitle="Project Ascentremit's unit economics for any cohort under any fee config."
      actions={<button onClick={run} disabled={loading} className="btn-primary !py-2 !px-4 text-sm" data-testid="stress-run-button"><ArrowsClockwise size={14} weight="bold" className={loading ? "animate-spin" : ""} /> Re-run</button>}>
      <div className="grid lg:grid-cols-12 gap-6 mb-8">
        {/* Inputs */}
        <div className="card lg:col-span-5" data-testid="stress-inputs">
          <div className="flex items-center gap-2 overline mb-2"><TestTube size={14} weight="bold" /> Cohort assumptions</div>
          <h3 className="font-display text-xl font-medium">Move the dials</h3>
          <p className="text-sm text-[#4F5D4F] mt-1">Each change re-runs the model on the next click.</p>
          <div className="mt-5 space-y-4">
            {SLIDERS.map((s) => (
              <div key={s.k}>
                <div className="flex items-baseline justify-between">
                  <label className="text-xs uppercase tracking-[0.14em] text-[#4F5D4F] font-semibold">{s.label}</label>
                  <span className="font-mono text-[#F2B632] text-sm font-medium">{s.fmt(inputs[s.k])}</span>
                </div>
                <input type="range" min={s.min} max={s.max} step={s.step} value={inputs[s.k]}
                  onChange={(e) => setInputs({ ...inputs, [s.k]: Number(e.target.value) })}
                  className="w-full accent-[#F2B632] mt-1" data-testid={`stress-slider-${s.k}`} />
              </div>
            ))}
          </div>

          <div className="divider my-5" />
          <div className="overline mb-3">Override fee config (optional)</div>
          <div className="grid grid-cols-2 gap-3">
            {FEE_OVERRIDES.map((f) => (
              <label key={f.k} className="block">
                <span className="text-[10.5px] uppercase tracking-[0.14em] text-[#4F5D4F] font-semibold">{f.label}</span>
                <input type="number" className="input mt-1 !py-2" placeholder={f.placeholder} value={overrides[f.k] ?? ""} onChange={(e) => setOverrides({ ...overrides, [f.k]: e.target.value })} data-testid={`stress-override-${f.k}`} />
              </label>
            ))}
          </div>
          <button onClick={run} disabled={loading} className="btn-primary mt-5 w-full justify-center" data-testid="stress-rerun-button">
            {loading ? "Running…" : "Run simulation"}
          </button>
        </div>

        {/* Results */}
        <div className="lg:col-span-7 space-y-6">
          {!t ? (
            <div className="card text-center py-12 text-sm text-[#4F5D4F]">Running first simulation…</div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4">
                <StatCard label="Projected GMV" value={fmtNGN(t.gmv)} sub={`${result.inputs.num_parents.toLocaleString()} parents · ${result.inputs.months} mo`} accent="primary" />
                <StatCard label="Platform revenue" value={fmtNGN(t.platform_revenue)} sub={`Take rate ${fmtBps(t.take_rate_bps)}`} accent="accent" />
                <StatCard label="Contribution margin" value={fmtNGN(t.contribution_margin)} sub={`${fmtNGN(t.contribution_per_parent)} / parent`} accent="success" />
                <StatCard label="NPL ratio" value={`${t.npl_ratio_pct}%`} sub={`${t.defaulters} of ${t.loan_users} loans`} accent="warning" />
              </div>

              <div className="card">
                <h4 className="font-display text-lg font-medium mb-2">Monthly P&L</h4>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={monthly}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E3DB" />
                    <XAxis dataKey="month" stroke="#4F5D4F" fontSize={12} />
                    <YAxis stroke="#4F5D4F" fontSize={12} tickFormatter={(v) => `${(v / 1000000).toFixed(1)}M`} />
                    <Tooltip formatter={(v) => fmtNGN(v)} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="platform_fee" name="Platform fee" stroke="#F2B632" strokeWidth={2.5} dot={false} />
                    <Line type="monotone" dataKey="contribution_margin" name="Contribution" stroke="#0A3622" strokeWidth={2.5} dot={false} />
                    <Line type="monotone" dataKey="dsa_payout" name="DSA payout" stroke="#B45309" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="defaults" name="Defaults" stroke="#C93B3B" strokeWidth={2} dot={false} strokeDasharray="4 4" />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="card">
                  <h4 className="font-display text-lg font-medium mb-2">Revenue stream stack</h4>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={[
                      { k: "Txn fee", v: t.platform_revenue - t.dsa_payout },
                      { k: "Late fees", v: t.late_fees },
                      { k: "Float", v: t.float_revenue },
                      { k: "Loan int.", v: t.interest_earned },
                      { k: "Defaults", v: -t.defaults },
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E3DB" />
                      <XAxis dataKey="k" stroke="#4F5D4F" fontSize={11} />
                      <YAxis stroke="#4F5D4F" fontSize={11} tickFormatter={(v) => `${(v / 1000000).toFixed(1)}M`} />
                      <Tooltip formatter={(v) => fmtNGN(v)} />
                      <Bar dataKey="v" fill="#0A3622" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="card text-sm space-y-2">
                  <h4 className="font-display text-lg font-medium mb-2">Bottom line</h4>
                  <Row k="Platform revenue (gross)" v={fmtNGN(t.platform_revenue)} />
                  <Row k="– DSA payouts" v={`-${fmtNGN(t.dsa_payout)}`} tone="warning" />
                  <Row k="+ Late fees" v={fmtNGN(t.late_fees)} />
                  <Row k="+ Wallet float spread" v={fmtNGN(t.float_revenue)} />
                  <Row k="+ Loan interest earned" v={fmtNGN(t.interest_earned)} />
                  <Row k="– Default losses" v={`-${fmtNGN(t.defaults)}`} tone="danger" />
                  <div className="divider my-2" />
                  <Row k="Contribution margin" v={fmtNGN(t.contribution_margin)} tone="success" emphasised />
                  <Row k="Per-parent margin" v={fmtNGN(t.contribution_per_parent)} />
                  <Row k="Effective take rate" v={fmtBps(t.take_rate_bps)} />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}

function Row({ k, v, tone = "neutral", emphasised }) {
  const colors = { neutral: "text-[#132A13]", success: "text-[#2D7A5D]", warning: "text-[#B45309]", danger: "text-[#C93B3B]" };
  return (
    <div className={`flex justify-between items-baseline ${emphasised ? "py-1.5 px-2 -mx-2 rounded-md bg-[#F0EFEA]" : ""}`}>
      <span className="text-[#4F5D4F]">{k}</span>
      <span className={`font-mono font-semibold ${colors[tone]}`}>{v}</span>
    </div>
  );
}
